/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * sponsorHeadings — the headings an organizer can put above a sponsor's logo,
 * and the level each one defaults to.
 *
 * Two columns drive the whole feature:
 *
 *   sponsor_label   the wording above the logo — "Title Sponsor", "Powered By"
 *   sponsor_level   1 to 100; WHERE the sponsor appears
 *
 * Level 1 is top billing: those sponsors are drawn in the strip under the hero
 * AND listed in the Sponsors section. Every other level is one row in that
 * section, lowest number first. Empty means unranked and sorts last.
 *
 * The level is what ranks a sponsor, not the wording and not the drag order.
 * That is deliberate: two sponsors can share a level without sharing wording,
 * so "Title Sponsor" and "Powered By" both sit under the hero, each keeping
 * its own caption. An event with no title sponsor — which is every AthloHive
 * event today — just marks "Powered By" level 1 and the strip fills.
 *
 * Each preset carries the level it almost always wants, so the organizer picks
 * a heading and never thinks about numbers. The level stays editable, and
 * "Other" exposes both: free text up to 64 characters and any level 1-100.
 *
 * Adding a heading here is one line and a deploy. It is NOT in the column's
 * allowed_options, so it never needs a migration, and a label set in Django
 * Admin that is not in this list still renders like any other.
 */

/** Free text plus a hand-set level. Not a stored value — see labelFromChoice. */
export const OTHER = '__other__';

export const MAX_LABEL_LENGTH = 64;
export const MIN_LEVEL = 1;
export const MAX_LEVEL = 100;

/** Headings grouped for the picker, each with the level it defaults to. */
export const SPONSOR_HEADING_GROUPS = [
  {
    label: 'Top billing — shown under the hero',
    options: [
      { label: 'Title Sponsor', level: 1 },
      { label: 'Presented By', level: 1 },
      { label: 'Powered By', level: 1 },
    ],
  },
  {
    label: 'Ranks',
    options: [
      { label: 'Platinum Sponsors', level: 2 },
      { label: 'Gold Sponsors', level: 3 },
      { label: 'Silver Sponsors', level: 4 },
      { label: 'Bronze Sponsors', level: 5 },
      { label: 'Associate Sponsors', level: 6 },
      { label: 'Co-Sponsors', level: 6 },
    ],
  },
  {
    label: 'On the course',
    options: [
      { label: 'Hydration Partner', level: 7 },
      { label: 'Nutrition Partner', level: 7 },
      { label: 'Timing Partner', level: 7 },
      { label: 'Medical Partner', level: 7 },
      { label: 'Ambulance Partner', level: 7 },
      { label: 'Recovery Partner', level: 7 },
      { label: 'Physio Partner', level: 7 },
      { label: 'Refreshment Partner', level: 7 },
      { label: 'Water Partner', level: 7 },
      { label: 'Security Partner', level: 7 },
    ],
  },
  {
    label: 'Kit and prizes',
    options: [
      { label: 'Apparel Partner', level: 7 },
      { label: 'Footwear Partner', level: 7 },
      { label: 'Gear Partner', level: 7 },
      { label: 'Goodie Bag Partner', level: 7 },
      { label: 'Medal Partner', level: 7 },
      { label: 'Trophy Partner', level: 7 },
      { label: 'Prize Partner', level: 7 },
      { label: 'Expo Partner', level: 7 },
    ],
  },
  {
    label: 'Reach',
    options: [
      { label: 'Media Partner', level: 7 },
      { label: 'Radio Partner', level: 7 },
      { label: 'Digital Partner', level: 7 },
      { label: 'Photography Partner', level: 7 },
      { label: 'Broadcast Partner', level: 7 },
      { label: 'App Partner', level: 7 },
    ],
  },
  {
    label: 'Behind the event',
    options: [
      { label: 'Hospitality Partner', level: 8 },
      { label: 'Venue Partner', level: 8 },
      { label: 'Travel Partner', level: 8 },
      { label: 'Logistics Partner', level: 8 },
      { label: 'Banking Partner', level: 8 },
      { label: 'Insurance Partner', level: 8 },
      { label: 'Payment Partner', level: 8 },
    ],
  },
  {
    // A yoga or studio event has no hydration station and no timing mat; its
    // sponsors are a different trade. Without these the list has nothing an
    // organizer of one could honestly pick.
    label: 'Fitness and wellness',
    options: [
      { label: 'Fitness Partner', level: 7 },
      { label: 'Training Partner', level: 7 },
      { label: 'Wellness Partner', level: 7 },
      { label: 'Yoga Partner', level: 7 },
      { label: 'Ayurveda Partner', level: 7 },
      { label: 'Music Partner', level: 7 },
    ],
  },
  {
    label: 'Endorsement',
    options: [
      { label: 'In Association With', level: 9 },
      { label: 'Supported By', level: 9 },
      { label: 'Promoted By', level: 9 },
      { label: 'Sanctioned By', level: 9 },
      { label: 'Tourism Partner', level: 9 },
      { label: 'Government Partner', level: 9 },
      { label: 'Charity Partner', level: 9 },
      { label: 'Green Partner', level: 9 },
      { label: 'Community Partner', level: 9 },
    ],
  },
];

/** Every preset heading, flat, for lookups. */
const PRESETS = SPONSOR_HEADING_GROUPS.flatMap((group) => group.options);

/** The level a preset heading defaults to, or null when it is not a preset. */
export const levelForHeading = (heading) => {
  const found = PRESETS.find((option) => option.label === heading);
  return found ? found.level : null;
};

/** Whether a stored label came from the list, which decides if the picker
 *  shows it as chosen or falls through to "Other". */
export const isPreset = (heading) => PRESETS.some((option) => option.label === heading);

/**
 * The groups in the `{ label, options: [{ value, label }] }` shape OrgSelect
 * wants, with the empty choice first and "Other" last.
 *
 * The empty choice is a real option, not placeholder text: OrgSelect has no
 * clear control, so without one an organizer who picked a heading by mistake
 * could never get back to having none. It is also the state every sponsor
 * written before these columns existed is already in.
 */
export const sponsorHeadingGroups = () => [
  { label: '', options: [{ value: '', label: 'None — just the logo' }] },
  ...SPONSOR_HEADING_GROUPS.map((group) => ({
    label: group.label,
    options: group.options.map((option) => ({
      value: option.label,
      label: `${option.label} · level ${option.level}`,
    })),
  })),
  { label: '', options: [{ value: OTHER, label: 'Other — write your own…' }] },
];

/**
 * The key two sponsors must share for their captions to count as the same
 * wording, used when deciding whether a level's row gets one heading or a
 * caption per card. Case and surrounding space are folded, because a label
 * typed in Django Admin should still match the one picked from the list.
 */
export const headingKey = (label) => String(label ?? '').trim().toLowerCase();

/** A level as a number, or null when it is blank or not a usable rank. */
export const parseLevel = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const level = Number(value);
  if (!Number.isInteger(level) || level < MIN_LEVEL || level > MAX_LEVEL) return null;
  return level;
};
